import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Params } from '@angular/router';
import { IAlbum, Lightbox } from 'ngx-lightbox';
import { StringHelper } from 'src/app/helpers/string-helper';
import Company from 'src/app/model/company/company';
import { Adjustment } from 'src/app/model/project/adjustment';
import { SUB_PROJECT_TYPES } from 'src/app/model/project/sub-project';
import { LoadTask } from 'src/app/model/task-list/load-task';
import { TASK_TYPES } from 'src/app/model/task-list/task';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';
import { CompanyDisplayDialogComponent } from 'src/app/modules/company/company-display-dialog/company-display-dialog.component';
import {
  GroupProjectContactPipe,
  ProjectContactGroup,
} from 'src/app/pipes/group-project-contact';
import { OfflineProjectStorageService } from 'src/app/services/offline-project-storage.service';
import { PlacerTasksService } from 'src/app/services/placer-tasks.service';
import { PlacerTaskChangesService } from '../../../../services/placer-offline-changes/task/offline-storage/placer-task-changes.service';
import {
  PlacerTaskSubmitService,
  SubmittedRegisteredActions,
} from '../../../../services/placer-offline-changes/task/submit/placer-task-submit.service';
import { SnackService } from 'src/app/services/snack.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import GeneralPlacerFormHelper from '../shared-helper/general-placer-form-helper';
import AdjustmentDisplay from './helper/adjustment-display';
import LoadTaskPlacerFormHelper, {
  LoadList,
} from './helper/load-task-placer-form-helper';
import OfflineTaskStorageHelper, {
  OfflineTaskStorageHelperContext,
} from '../shared-helper/offline-task-storage-helper';
import ProductDisplay from './helper/product-display';
import { OfflineTaskChangesObject } from 'src/app/services/placer-offline-changes/task/offline-storage/offline-task-changes-object';
import { Subject } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { REGISTERED_ACTION_TYPES } from 'src/app/model/registered-action-type';
import ErrorHelper from 'src/app/helpers/error-helper';
import { SubmitEventService } from 'src/app/services/placer-offline-changes/submit-event.service';
import SubmitQueueHelper from 'src/app/helpers/offline-submit/submit-queue-helper';
import RegisteredAction from 'src/app/model/registered-action';

@Component({
  selector: 'load-task-placer-form',
  templateUrl: './load-task-placer-form.component.html',
  styleUrls: [
    '../placer-form-shared-style.scss',
    './load-task-placer-form.component.scss',
  ],
  providers: [FormatPhoneNumber, GroupProjectContactPipe],
})
export class LoadTaskPlacerFormComponent
  extends SubscriptionBasedComponent
  implements OnInit, OnDestroy
{
  immoscoopLogo: boolean;
  spottoLogo: boolean;
  inpressLogo: boolean;

  private employeeId: number;
  private dateString: string;
  private taskId: number;

  init: boolean = false;

  taskStarted: boolean = false;
  disallowStartTask: boolean = false;

  task: LoadTask;
  projectContactGroups: ProjectContactGroup[];

  bannerText: string;
  bannerClass: string;
  timeBudget: string;

  productDisplay: ProductDisplay;
  adjustmentDisplay: AdjustmentDisplay;
  extraMaterialsInfo: string;

  loadList: LoadList;

  storageHelperContext: OfflineTaskStorageHelperContext;
  taskChanges: OfflineTaskChangesObject;

  private storeOfflineChangeTrigger: Subject<any>;
  storingOfflineChange: boolean = false;

  everythingLoaded: boolean = false;

  allowStart: boolean = true;
  submitting: boolean = false;
  displayExecutionTime: boolean = false;

  executionTimeString: string;

  pendingSubmitRegisteredActions: boolean = false;
  submitInProgress: boolean = false;
  submitQueued: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private snackService: SnackService,
    private authService: AuthService,
    private placerTasksService: PlacerTasksService,
    private offlineProjectStorageService: OfflineProjectStorageService,
    private _lightbox: Lightbox,
    private formatPhoneNumber: FormatPhoneNumber,
    private groupProjectContactsPipe: GroupProjectContactPipe,
    private placerTaskChangesService: PlacerTaskChangesService,
    private placerTaskSubmitService: PlacerTaskSubmitService,
    private submitEventService: SubmitEventService,
    private dialog: MatDialog
  ) {
    super();
  }

  ngOnInit(): void {
    this.initialize()
      .then(() => (this.init = true))
      .catch((err) => {
        console.error(err);
        if (err instanceof Error) {
          this.snackService.showError(err.message);
        } else {
          this.snackService.showError(err);
        }
      });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.storeOfflineChangeTrigger != null) {
      this.storeOfflineChangeTrigger.complete();
    }
  }

  private initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.addSubscription(
        this.route.params,
        async (params: Params) => {
          try {
            this.processRouteParams(params);
            this.storageHelperContext = {
              placerTaskChangesService: this.placerTaskChangesService,
            };
            await this.initializeTaskDisplay();
            this.taskChanges = await OfflineTaskStorageHelper.getOfflineChanges(
              this.task,
              this.storageHelperContext
            );
            this.processExistingOfflineChanges();
            this.initializeLoadListitems();
            this.initializeOfflineStorageChangeTrigger();
            this.initializeRegisteredActionsSubmittedSubscription();
            this.projectContactGroups = this.groupProjectContactsPipe.transform(
              this.task.subProject.project.projectContacts
            );
            await this.initializeSubmitInfo();
            resolve();
          } catch (err) {
            reject(err);
          }
        },
        (err) => reject(err)
      );
    });
  }

  private processRouteParams(params: Params): void {
    this.employeeId =
      params['employeeId'] == null ? null : Number(params['employeeId']);
    if (this.employeeId == null) {
      throw new Error('employeeId required but is missing');
    }
    this.dateString = params['date'] == null ? null : params['date'];
    if (StringHelper.isEmpty(this.dateString)) {
      throw new Error('date required but is missing');
    }
    this.taskId = params['taskId'] == null ? null : Number(params['taskId']);
    if (this.taskId == null) {
      throw new Error('taskId required but is missing');
    }
  }

  private async initializeTaskDisplay(): Promise<void> {
    const offlineTask = await this.placerTasksService.getTaskFromOffline(
      this.employeeId,
      this.dateString,
      this.taskId
    );
    if (offlineTask == null) {
      throw new Error(
        `task not found for employeeId: ${this.employeeId}, dateString: ${this.dateString}, taskId: ${this.taskId}`
      );
    }
    if (offlineTask.type !== TASK_TYPES.LOAD_TASK) {
      throw new Error(`task with id ${offlineTask.id} was not a LoadTask`);
    }

    this.task = <LoadTask>offlineTask;
    await GeneralPlacerFormHelper.loadProjectForTask(
      this.task,
      this.offlineProjectStorageService
    );

    this.initializeSimpleDisplays();

    if (
      this.task.subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT &&
      this.task.product != null
    ) {
      this.productDisplay = LoadTaskPlacerFormHelper.getProductDisplay(
        this.task.product,
        { formatPhoneNumber: this.formatPhoneNumber }
      );
      this.extraMaterialsInfo =
        LoadTaskPlacerFormHelper.getExtraInfoNeedMaterialsProduct(
          this.task.product
        );
    }
    if (this.task.subProject.type !== SUB_PROJECT_TYPES.BASE_PROJECT) {
      this.adjustmentDisplay = LoadTaskPlacerFormHelper.getAdjustmentDisplay(
        this.task
      );
      this.extraMaterialsInfo =
        LoadTaskPlacerFormHelper.getExtraInfoNeedMaterialsAdjustment(
          <Adjustment>this.task.subProject
        );
    }
  }

  private initializeSimpleDisplays() {
    this.bannerText = GeneralPlacerFormHelper.getBannerText(
      this.task.subProject
    );
    this.bannerClass = GeneralPlacerFormHelper.getBannerClass(
      this.task.subProject
    );
    this.timeBudget = GeneralPlacerFormHelper.getTimeBudget(this.task);
    this.immoscoopLogo = LoadTaskPlacerFormHelper.getImmoscoopLogo(this.task);
    this.spottoLogo = LoadTaskPlacerFormHelper.getSpottopLogo(this.task);
    this.inpressLogo = LoadTaskPlacerFormHelper.getInpressLogo(this.task);
  }

  private processExistingOfflineChanges(): void {
    this.task.complete =
      this.taskChanges.complete == null
        ? this.task.complete
        : this.taskChanges.complete;
    OfflineTaskStorageHelper.processOfflineRegisteredActions(
      this.task,
      this.taskChanges
    );
    this.taskStarted = GeneralPlacerFormHelper.isTaskStarted(this.task);
    if (this.task.complete) {
      this.disallowStartTask = true;
      this.executionTimeString =
        GeneralPlacerFormHelper.getTaskExecutedTimeString(
          this.task.registeredActions
        );
      this.displayExecutionTime = true;
    }
  }

  private initializeLoadListitems(): void {
    this.loadList = LoadTaskPlacerFormHelper.initializeLoadListItems({
      loadTask: this.task,
      taskChanges: this.taskChanges,
      immoscoopLogo: this.immoscoopLogo,
      spottoLogo: this.spottoLogo,
      inpressLogo: this.inpressLogo,
    });
    this.everythingLoaded = LoadTaskPlacerFormHelper.isEverythingLoaded(
      this.loadList
    );
  }

  private initializeOfflineStorageChangeTrigger(): void {
    this.storeOfflineChangeTrigger = new Subject<any>();
    this.addSubscription(
      this.storeOfflineChangeTrigger,
      () => {
        this.storingOfflineChange = true;
        this.placerTaskChangesService
          .storeObject(this.taskChanges)
          .then(async () => {
            if (this.pendingSubmitRegisteredActions) {
              this.pendingSubmitRegisteredActions = false;
              this.placerTaskSubmitService
                .submitRegisteredActions(this.taskChanges, {
                  employeeId: this.employeeId,
                  dateString: this.dateString,
                })
                .then((registeredActions: RegisteredAction[]) => {
                  this.task.registeredActions = registeredActions;
                })
                .catch((err) => console.error(err));
            }
          })
          .catch((err) => console.error(err))
          .finally(() => {
            this.storingOfflineChange = false;
          });
      },
      () => {},
      GeneralPlacerFormHelper.STORE_OFFLINE_CHANGE_DELAY
    );
  }

  private initializeRegisteredActionsSubmittedSubscription(): void {
    this.addSubscription(
      this.placerTaskSubmitService.getRegisteredActionsSubmittedForTaskSubscribable(),
      (submitted: SubmittedRegisteredActions) => {
        if (submitted.taskId === this.task.id) {
          this.task.registeredActions = submitted.registeredActions;
        }
      }
    );
  }

  private async initializeSubmitInfo(): Promise<void> {
    const promises = [
      this.calculateSubmitInProgress(),
      this.calculateSubmitQueued(),
    ];
    await Promise.all(promises);
    this.addSubscription(
      this.submitEventService.getSubmitInProgressChangedSubscribable(),
      () => {
        this.calculateSubmitInProgress();
      }
    );
    this.addSubscription(
      this.placerTaskSubmitService.getToSubmitChangedSubscribable(),
      () => {
        this.calculateSubmitQueued();
      }
    );
  }

  private async calculateSubmitInProgress(): Promise<void> {
    const inProgress = await this.submitEventService.getSubmitInProgressAsync();
    this.submitInProgress = SubmitQueueHelper.evaluateTaskInProgress(
      this.task,
      inProgress
    );
  }

  private async calculateSubmitQueued(): Promise<void> {
    const queued = await this.placerTaskSubmitService.getToSubmit();
    this.submitQueued = SubmitQueueHelper.evaluateTaskQueued(this.task, queued);
  }

  startTask(): void {
    this.taskStarted = true;
    GeneralPlacerFormHelper.addTaskRegisteredAction(
      this.task,
      REGISTERED_ACTION_TYPES.START_PROJECT_EXECUTION,
      this.authService.getAuthenticatedUser()
    );
    this.pendingSubmitRegisteredActions = true;
    this.storingOfflineChange = true;
    this.saveToOfflineStorage();
  }

  tilingLoadedChange(): void {
    this.processChange();
  }

  immoscoopAndOrSpottoAndOrInpressLoadedChange(): void {
    this.processChange();
  }

  materialLoadChange(): void {
    this.processChange();
  }

  private processChange() {
    this.storingOfflineChange = true;
    this.saveToOfflineStorage();
    this.everythingLoaded = LoadTaskPlacerFormHelper.isEverythingLoaded(
      this.loadList
    );
  }

  private saveToOfflineStorage(): void {
    OfflineTaskStorageHelper.copyToTaskChanges(
      this.task,
      this.taskChanges,
      this.loadList
    );
    this.storeOfflineChangeTrigger.next();
  }

  private async immediateSaveToOfflineStorage(): Promise<void> {
    OfflineTaskStorageHelper.copyToTaskChanges(
      this.task,
      this.taskChanges,
      this.loadList
    );
    await this.placerTaskChangesService.storeObject(this.taskChanges);
  }

  openCompanyDisplayDialog(company: Company) {
    const config = {
      company: company,
    };
    this.dialog.open(CompanyDisplayDialogComponent, {
      data: config,
    });
  }

  openAlbumLightbox(album: IAlbum[], index: number) {
    this._lightbox.open(album, index);
  }

  async submit(): Promise<void> {
    this.submitting = true;
    this.task.complete = true;
    GeneralPlacerFormHelper.addTaskRegisteredAction(
      this.task,
      REGISTERED_ACTION_TYPES.STOP_PROJECT_EXECUTION,
      this.authService.getAuthenticatedUser()
    );
    this.executionTimeString =
      GeneralPlacerFormHelper.getTaskExecutedTimeString(
        this.task.registeredActions
      );
    this.displayExecutionTime = true;

    try {
      await this.immediateSaveToOfflineStorage();
      const responseTask = await this.placerTaskSubmitService.submitTask(
        this.taskChanges,
        { employeeId: this.employeeId, dateString: this.dateString }
      );
      this.task.registeredActions = responseTask.registeredActions;
      this.snackService.showSuccess('Laadmoment succesvol ingediend');
    } catch (err) {
      if (!ErrorHelper.isConnectionLostError(err)) {
        console.error(err);
        if (err instanceof Error) {
          this.snackService.showError(err.message);
        } else {
          this.snackService.showError(err);
        }
      }
    } finally {
      this.submitting = false;
    }
  }

  async retrySubmit(): Promise<void> {
    this.submitting = true;
    try {
      await this.placerTaskSubmitService.retrySubmit(this.task.id, {
        employeeId: this.employeeId,
        dateString: this.dateString,
      });
    } catch (err) {
      console.error(err);
      this.snackService.showError(err);
    } finally {
      this.submitting = false;
    }
  }
}
